import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { MapPinIcon, ClockIcon, PhoneIcon, MailIcon, GlobeIcon, ArrowLeftIcon, CheckCircleIcon, StarIcon, UsersIcon, TagIcon, FilmIcon, VideoIcon, AwardIcon, MessageSquareIcon, ChevronDownIcon, InstagramIcon, FacebookIcon } from 'lucide-react';
import { getBusinessBySlug, getRelatedBusinesses } from '../data/businesses';
import { Footer } from '../components/Footer';
import { Lightbox } from '../components/Lightbox';
import { MenuShowcase } from '../components/MenuShowcase';
import { useLanguage } from '../context/LanguageContext';
interface BusinessDetailPageProps {
  slug: string;
  onNavigate: (page: 'home' | 'about' | 'contact' | 'business', slug?: string) => void;
}
export function BusinessDetailPage({
  slug,
  onNavigate
}: BusinessDetailPageProps) {
  const business = getBusinessBySlug(slug);
  const relatedBusinesses = getRelatedBusinesses(slug);
  const [showAllGallery, setShowAllGallery] = useState(false);
  const [showAllMovies, setShowAllMovies] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const {
    t
  } = useLanguage();
  if (!business) {
    return <div className="min-h-screen flex items-center justify-center bg-white pt-16" data-id="element-366">
        <div className="text-center" data-id="element-367">
          <h2 className="font-display text-[32px] font-medium text-[#1d1d1f] mb-4" data-id="element-368">
            Business not found
          </h2>
          <button onClick={() => onNavigate('home')} className="text-[#c9a961] font-medium hover:underline" data-id="element-369">
            Return Home
          </button>
        </div>
      </div>;
  }
  const galleryImages = business.gallery || [];
  const visibleGallery = showAllGallery ? galleryImages : galleryImages.slice(0, 6);
  const moviesList = business.movies || [];
  const visibleMovies = showAllMovies ? moviesList : moviesList.slice(0, 20);
  const hasRichContent = business.movies || business.tvcs || business.gallery;
  const openLightbox = (index: number) => {
    setLightboxIndex(index);
    setIsLightboxOpen(true);
  };
  return <main className="min-h-screen bg-white" data-id="element-370">
      {/* Hero Section */}
      <section className="relative h-[70vh] min-h-[600px] w-full overflow-hidden bg-black" data-id="element-371">
        {business.image && <div className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-40 animate-[zoom_20s_infinite_alternate]" style={{
        backgroundImage: `url('${business.image}')`,
        transform: 'scale(1.1)'
      }} data-id="element-372" />}
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-black/30" data-id="element-373" />

        <div className="relative h-full max-w-7xl mx-auto px-6 flex flex-col justify-end pb-16 md:pb-24" data-id="element-374">
          <motion.button initial={{
          opacity: 0,
          x: -10
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.5,
          delay: 0.1
        }} onClick={() => onNavigate('home')} className="absolute top-24 left-6 text-white/60 hover:text-[#c9a961] text-[14px] uppercase tracking-wider font-medium flex items-center gap-2 transition-colors duration-300" data-id="element-375">
            <ArrowLeftIcon className="w-4 h-4" data-id="element-376" />
            {t.common.backToPortfolio}
          </motion.button>

          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 0.2
        }} data-id="element-377">
            <span className="inline-block px-4 py-1.5 rounded-sm bg-[#c9a961] text-black text-[12px] uppercase tracking-widest font-bold mb-6" data-id="element-378">
              {business.category}
            </span>
            <h1 className="font-display text-white text-[48px] md:text-[72px] lg:text-[88px] font-medium leading-[1.1] tracking-tight mb-6" data-id="element-379">
              {business.name}
            </h1>
            {hasRichContent && <p className="text-white/80 text-[18px] md:text-[20px] mb-8 max-w-2xl font-light leading-relaxed" data-id="element-380">
                {business.description}
              </p>}
            <div className="flex flex-wrap gap-8 text-white/80 text-[15px] tracking-wide" data-id="element-381">
              <div className="flex items-center gap-2" data-id="element-382">
                <MapPinIcon className="w-5 h-5 text-[#c9a961]" data-id="element-383" />
                {business.location}
              </div>
              {business.establishedYear && <div className="flex items-center gap-2" data-id="element-384">
                  <AwardIcon className="w-5 h-5 text-[#c9a961]" data-id="element-385" />
                  {t.common.established} {business.establishedYear}
                </div>}
              {business.hours && <div className="flex items-center gap-2" data-id="element-386">
                  <ClockIcon className="w-5 h-5 text-[#c9a961]" data-id="element-387" />
                  {business.hours}
                </div>}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Stats Bar */}
      {hasRichContent && <section className="bg-[#1d1d1f] py-16 px-6 border-t border-white/5" data-id="element-388">
          <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-16" data-id="element-389">
            {business.movies && <div className="text-center" data-id="element-390">
                <div className="font-display text-[48px] md:text-[64px] font-medium text-white leading-none mb-2" data-id="element-391">
                  {business.movies.length}+
                </div>
                <div className="text-[#86868b] text-[13px] uppercase tracking-widest" data-id="element-392">
                  {t.common.filmography}
                </div>
              </div>}
            {business.tvcs && <div className="text-center" data-id="element-393">
                <div className="font-display text-[48px] md:text-[64px] font-medium text-white leading-none mb-2" data-id="element-394">
                  {business.tvcs.length}+
                </div>
                <div className="text-[#86868b] text-[13px] uppercase tracking-widest" data-id="element-395">
                  {t.common.tvcs}
                </div>
              </div>}
            {business.gallery && <div className="text-center" data-id="element-396">
                <div className="font-display text-[48px] md:text-[64px] font-medium text-white leading-none mb-2" data-id="element-397">
                  500+
                </div>
                <div className="text-[#86868b] text-[13px] uppercase tracking-widest" data-id="element-398">
                  Projects Completed
                </div>
              </div>}
            {business.establishedYear && <div className="text-center" data-id="element-399">
                <div className="font-display text-[48px] md:text-[64px] font-medium text-[#c9a961] leading-none mb-2" data-id="element-400">
                  {business.establishedYear}
                </div>
                <div className="text-[#86868b] text-[13px] uppercase tracking-widest" data-id="element-401">
                  {t.common.established}
                </div>
              </div>}
          </div>
        </section>}

      {/* Quick Info Bar */}
      <section className="bg-[#f2f2f4] py-10 px-6 border-b border-[#c9a961]/10" data-id="element-402">
        <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-8" data-id="element-403">
          <div className="flex items-center gap-4" data-id="element-404">
            <TagIcon className="w-5 h-5 text-[#c9a961]" data-id="element-405" />
            <div data-id="element-406">
              <span className="block text-[11px] text-[#86868b] uppercase tracking-widest mb-1" data-id="element-407">
                Category
              </span>
              <span className="text-[#1d1d1f] font-medium text-[15px]" data-id="element-408">
                {business.category}
              </span>
            </div>
          </div>
          {business.hours && <div className="flex items-center gap-4" data-id="element-409">
              <ClockIcon className="w-5 h-5 text-[#c9a961]" data-id="element-410" />
              <div data-id="element-411">
                <span className="block text-[11px] text-[#86868b] uppercase tracking-widest mb-1" data-id="element-412">
                  {t.common.hours}
                </span>
                <span className="text-[#1d1d1f] font-medium text-[15px]" data-id="element-413">
                  {business.hours}
                </span>
              </div>
            </div>}
          {business.capacity && <div className="flex items-center gap-4" data-id="element-414">
              <UsersIcon className="w-5 h-5 text-[#c9a961]" data-id="element-415" />
              <div data-id="element-416">
                <span className="block text-[11px] text-[#86868b] uppercase tracking-widest mb-1" data-id="element-417">
                  {t.common.capacity}
                </span>
                <span className="text-[#1d1d1f] font-medium text-[15px]" data-id="element-418">
                  {business.capacity} Guests
                </span>
              </div>
            </div>}
          {business.contactNumber && <div className="flex items-center gap-4" data-id="element-419">
              <PhoneIcon className="w-5 h-5 text-[#c9a961]" data-id="element-420" />
              <div data-id="element-421">
                <span className="block text-[11px] text-[#86868b] uppercase tracking-widest mb-1" data-id="element-422">
                  {t.common.phone}
                </span>
                <a href={`tel:${business.contactNumber.split('/')[0].trim()}`} className="text-[#1d1d1f] font-medium text-[15px] hover:text-[#c9a961] transition-colors" data-id="element-423">
                  {business.contactNumber.split('/')[0].trim()}
                </a>
              </div>
            </div>}
        </div>
      </section>

      {/* Content Grid */}
      <section className="py-24 md:py-32 px-6 bg-white" data-id="element-424">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-12 lg:gap-20" data-id="element-425">
          {/* Main Content */}
          <div className="lg:col-span-8" data-id="element-426">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="mb-16" data-id="element-427">
              <span className="text-[#c9a961] text-[13px] font-bold tracking-[0.2em] uppercase mb-4 block" data-id="element-428">
                {t.common.overview}
              </span>
              <h2 className="font-display text-[32px] md:text-[48px] font-medium text-[#1d1d1f] mb-6" data-id="element-429">
                About The Venue
              </h2>
              <p className="text-[#86868b] text-[18px] md:text-[20px] leading-[1.8] font-light" data-id="element-430">
                {business.longDescription || business.description}
              </p>
            </motion.div>

            {/* Services/Features */}
            {(business.services || business.features) && <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="mb-16" data-id="element-431">
                <h2 className="font-display text-[28px] font-medium text-[#1d1d1f] mb-8" data-id="element-432">
                  {business.category === 'Entertainment' ? 'Our Services' : 'What We Offer'}
                </h2>
                <div className="grid sm:grid-cols-2 gap-4" data-id="element-433">
                  {[...(business.services || []), ...(business.features || [])].map((item, idx) => <div key={idx} className="flex items-center gap-4 p-5 rounded-sm bg-[#ebebed] border border-[#d8d8dc]" data-id="element-434">
                      <CheckCircleIcon className="w-5 h-5 text-[#c9a961] shrink-0" data-id="element-435" />
                      <span className="text-[#1d1d1f] text-[15px] font-medium" data-id="element-436">
                        {item}
                      </span>
                    </div>)}
                </div>
              </motion.div>}

            {/* Menu Showcase */}
            {business.menuItems && <MenuShowcase items={business.menuItems} data-id="element-437" />}

            {/* Gallery */}
            {galleryImages.length > 0 && <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="mb-16" data-id="element-438">
                <h2 className="font-display text-[28px] font-medium text-[#1d1d1f] mb-8" data-id="element-439">
                  {t.common.gallery}
                </h2>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4" data-id="element-440">
                  {visibleGallery.map((img, idx) => <div key={idx} onClick={() => openLightbox(idx)} className="aspect-[4/3] rounded-sm overflow-hidden bg-[#f2f2f4] relative group cursor-pointer" data-id="element-441">
                      <img src={img.url} alt={img.alt} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" data-id="element-442" />
                      <div className="absolute inset-0 bg-black/20 group-hover:bg-transparent transition-colors duration-300" data-id="element-443" />
                      {img.caption && <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-6" data-id="element-444">
                          <p className="text-white text-[13px] font-medium tracking-wide" data-id="element-445">
                            {img.caption}
                          </p>
                        </div>}
                    </div>)}
                </div>
                {galleryImages.length > 6 && <button onClick={() => setShowAllGallery(!showAllGallery)} className="mt-8 mx-auto flex items-center gap-2 text-[#c9a961] font-medium text-[14px] uppercase tracking-wide hover:gap-3 transition-all duration-300" data-id="element-446">
                    {showAllGallery ? 'Show Less' : `View All ${galleryImages.length} Photos`}
                    <ChevronDownIcon className={`w-4 h-4 transition-transform duration-300 ${showAllGallery ? 'rotate-180' : ''}`} data-id="element-447" />
                  </button>}
              </motion.div>}

            {/* Filmography */}
            {moviesList.length > 0 && <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="mb-16" data-id="element-448">
                <h2 className="font-display text-[28px] font-medium text-[#1d1d1f] mb-8 flex items-center gap-3" data-id="element-449">
                  <FilmIcon className="w-6 h-6 text-[#c9a961]" data-id="element-450" />
                  {t.common.filmography}
                </h2>
                <div className="grid sm:grid-cols-2 gap-x-12 gap-y-0" data-id="element-451">
                  {visibleMovies.map((movie, idx) => <div key={idx} className="flex items-center gap-4 py-4 border-b border-[#d8d8dc]" data-id="element-452">
                      <span className="text-[#c9a961] text-[13px] font-mono w-6 shrink-0" data-id="element-453">
                        {String(idx + 1).padStart(2, '0')}
                      </span>
                      <span className="text-[#1d1d1f] text-[15px]" data-id="element-454">
                        {movie}
                      </span>
                    </div>)}
                </div>
                {moviesList.length > 20 && <button onClick={() => setShowAllMovies(!showAllMovies)} className="mt-8 flex items-center gap-2 text-[#c9a961] font-medium text-[14px] uppercase tracking-wide hover:gap-3 transition-all duration-300" data-id="element-455">
                    {showAllMovies ? 'Show Less' : `View All ${moviesList.length} Productions`}
                    <ChevronDownIcon className={`w-4 h-4 transition-transform duration-300 ${showAllMovies ? 'rotate-180' : ''}`} data-id="element-456" />
                  </button>}
              </motion.div>}

            {/* TVCs */}
            {business.tvcs && business.tvcs.length > 0 && <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="mb-16" data-id="element-457">
                <h2 className="font-display text-[28px] font-medium text-[#1d1d1f] mb-8 flex items-center gap-3" data-id="element-458">
                  <VideoIcon className="w-6 h-6 text-[#c9a961]" data-id="element-459" />
                  {t.common.tvcs}
                </h2>
                <div className="flex flex-wrap gap-3" data-id="element-460">
                  {business.tvcs.map((tvc, idx) => <span key={idx} className="px-5 py-2 bg-[#f2f2f4] border border-[#d8d8dc] rounded-full text-[#1d1d1f] text-[14px] font-medium" data-id="element-461">
                      {tvc}
                    </span>)}
                </div>
              </motion.div>}

            {/* Reviews */}
            {business.reviews && business.reviews.length > 0 && <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} data-id="element-462">
                <h2 className="font-display text-[28px] font-medium text-[#1d1d1f] mb-8" data-id="element-463">
                  {t.common.reviews}
                </h2>
                <div className="grid gap-8" data-id="element-464">
                  {business.reviews.map((review, idx) => <div key={idx} className="p-8 rounded-sm bg-[#ebebed] border border-[#d8d8dc]" data-id="element-465">
                      <div className="flex gap-1 mb-6" data-id="element-466">
                        {[...Array(5)].map((_, i) => <StarIcon key={i} className={`w-4 h-4 ${i < review.rating ? 'fill-[#c9a961] text-[#c9a961]' : 'text-[#d2d2d7]'}`} data-id="element-467" />)}
                      </div>
                      <p className="text-[#1d1d1f] text-[18px] font-light italic leading-relaxed mb-6" data-id="element-468">
                        "{review.text}"
                      </p>
                      <div className="flex justify-between items-center border-t border-[#c9a961]/10 pt-4" data-id="element-469">
                        <span className="font-display font-medium text-[#1d1d1f] text-[16px]" data-id="element-470">
                          {review.author}
                        </span>
                        <span className="text-[#86868b] text-[13px] uppercase tracking-wide" data-id="element-471">
                          {review.date}
                        </span>
                      </div>
                    </div>)}
                </div>
              </motion.div>}
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-4" data-id="element-472">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6,
            delay: 0.1
          }} className="sticky top-24 space-y-8" data-id="element-473">
              <div className="p-8 rounded-sm bg-[#f2f2f4] border border-[#d8d8dc]" data-id="element-474">
                <h3 className="font-display text-[20px] font-medium text-[#1d1d1f] mb-6" data-id="element-475">
                  {t.common.contactInfo}
                </h3>
                <div className="space-y-6" data-id="element-476">
                  <div className="flex gap-4" data-id="element-477">
                    <MapPinIcon className="w-5 h-5 text-[#c9a961] shrink-0 mt-0.5" data-id="element-478" />
                    <div data-id="element-479">
                      <span className="block text-[11px] text-[#86868b] uppercase tracking-widest mb-1" data-id="element-480">
                        {t.common.address}
                      </span>
                      <span className="text-[#1d1d1f] text-[15px] leading-relaxed" data-id="element-481">
                        {business.address}
                      </span>
                    </div>
                  </div>

                  {business.managingDirector && <div className="flex gap-4" data-id="element-482">
                      <UsersIcon className="w-5 h-5 text-[#c9a961] shrink-0 mt-0.5" data-id="element-483" />
                      <div data-id="element-484">
                        <span className="block text-[11px] text-[#86868b] uppercase tracking-widest mb-1" data-id="element-485">
                          Managing Director
                        </span>
                        <span className="text-[#1d1d1f] text-[15px]" data-id="element-486">
                          {business.managingDirector}
                        </span>
                      </div>
                    </div>}

                  {business.contactPerson && !business.managingDirector && <div className="flex gap-4" data-id="element-487">
                      <UsersIcon className="w-5 h-5 text-[#c9a961] shrink-0 mt-0.5" data-id="element-488" />
                      <div data-id="element-489">
                        <span className="block text-[11px] text-[#86868b] uppercase tracking-widest mb-1" data-id="element-490">
                          Contact Person
                        </span>
                        <span className="text-[#1d1d1f] text-[15px]" data-id="element-491">
                          {business.contactPerson}
                        </span>
                      </div>
                    </div>}

                  {business.contactNumber && <div className="flex gap-4" data-id="element-492">
                      <PhoneIcon className="w-5 h-5 text-[#c9a961] shrink-0 mt-0.5" data-id="element-493" />
                      <div data-id="element-494">
                        <span className="block text-[11px] text-[#86868b] uppercase tracking-widest mb-1" data-id="element-495">
                          {t.common.phone}
                        </span>
                        <a href={`tel:${business.contactNumber.split('/')[0].trim()}`} className="text-[#1d1d1f] text-[15px] hover:text-[#c9a961] transition-colors" data-id="element-496">
                          {business.contactNumber}
                        </a>
                      </div>
                    </div>}

                  {business.email && <div className="flex gap-4" data-id="element-497">
                      <MailIcon className="w-5 h-5 text-[#c9a961] shrink-0 mt-0.5" data-id="element-498" />
                      <div data-id="element-499">
                        <span className="block text-[11px] text-[#86868b] uppercase tracking-widest mb-1" data-id="element-500">
                          {t.common.email}
                        </span>
                        <a href={`mailto:${business.email}`} className="text-[#1d1d1f] text-[15px] hover:text-[#c9a961] transition-colors break-all" data-id="element-501">
                          {business.email}
                        </a>
                      </div>
                    </div>}

                  {business.lineId && <div className="flex gap-4" data-id="element-502">
                      <MessageSquareIcon className="w-5 h-5 text-[#c9a961] shrink-0 mt-0.5" data-id="element-503" />
                      <div data-id="element-504">
                        <span className="block text-[11px] text-[#86868b] uppercase tracking-widest mb-1" data-id="element-505">
                          Line ID
                        </span>
                        <span className="text-[#1d1d1f] text-[15px]" data-id="element-506">
                          {business.lineId}
                        </span>
                      </div>
                    </div>}

                  {business.website && <div className="flex gap-4" data-id="element-507">
                      <GlobeIcon className="w-5 h-5 text-[#c9a961] shrink-0 mt-0.5" data-id="element-508" />
                      <div data-id="element-509">
                        <span className="block text-[11px] text-[#86868b] uppercase tracking-widest mb-1" data-id="element-510">
                          {t.common.website}
                        </span>
                        <a href={`https://${business.website}`} target="_blank" rel="noopener noreferrer" className="text-[#1d1d1f] text-[15px] hover:text-[#c9a961] transition-colors" data-id="element-511">
                          {business.website}
                        </a>
                      </div>
                    </div>}
                </div>

                {/* Social Media Links */}
                <div className="mt-8 pt-8 border-t border-[#e5e5e5] flex gap-4" data-id="element-512">
                  {business.instagram && <a href={`https://instagram.com/${business.instagram}`} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white border border-[#e5e5e5] flex items-center justify-center text-[#1d1d1f] hover:bg-[#c9a961] hover:text-white hover:border-[#c9a961] transition-all duration-300" data-id="element-513">
                      <InstagramIcon className="w-5 h-5" data-id="element-514" />
                    </a>}
                  {business.facebook && <a href={`https://facebook.com/${business.facebook}`} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white border border-[#e5e5e5] flex items-center justify-center text-[#1d1d1f] hover:bg-[#c9a961] hover:text-white hover:border-[#c9a961] transition-all duration-300" data-id="element-515">
                      <FacebookIcon className="w-5 h-5" data-id="element-516" />
                    </a>}
                </div>
              </div>

              {business.registrations && <div className="p-8 rounded-sm bg-white border border-[#e5e5e5]" data-id="element-517">
                  <h3 className="font-display text-[20px] font-medium text-[#1d1d1f] mb-6 flex items-center gap-2" data-id="element-518">
                    <AwardIcon className="w-5 h-5 text-[#c9a961]" data-id="element-519" />
                    Registrations
                  </h3>
                  <ul className="space-y-4" data-id="element-520">
                    {business.registrations.map((reg, idx) => <li key={idx} className="flex items-center gap-3 text-[#1d1d1f] text-[15px]" data-id="element-521">
                        <CheckCircleIcon className="w-4 h-4 text-[#c9a961]" data-id="element-522" />
                        {reg}
                      </li>)}
                  </ul>
                </div>}

              {business.establishedYear && <div className="p-8 rounded-sm bg-[#1d1d1f] text-white" data-id="element-523">
                  <span className="block text-white/50 text-[11px] uppercase tracking-widest mb-2" data-id="element-524">
                    {t.common.established}
                  </span>
                  <div className="font-display text-[48px] font-medium leading-none mb-1 text-[#c9a961]" data-id="element-525">
                    {business.establishedYear}
                  </div>
                  <div className="text-white/60 text-[15px]" data-id="element-526">
                    Bangkok, Thailand
                  </div>
                </div>}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Related Businesses */}
      {relatedBusinesses.length > 0 && <section className="py-24 md:py-32 px-6 bg-[#f2f2f4] border-t border-[#c9a961]/10" data-id="element-527">
          <div className="max-w-7xl mx-auto" data-id="element-528">
            <h2 className="font-display text-[32px] md:text-[48px] font-medium text-[#1d1d1f] mb-4 text-center" data-id="element-529">
              More {business.category}
            </h2>
            <div className="w-16 h-[1px] bg-[#c9a961] mx-auto mb-8" data-id="element-530" />
            <p className="text-[#86868b] text-[18px] max-w-3xl mx-auto text-center mb-16 font-light" data-id="element-531">
              Explore similar venues in our portfolio
            </p>
            <div className="grid md:grid-cols-3 gap-8" data-id="element-532">
              {relatedBusinesses.map((related, idx) => <motion.div key={related.slug} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: idx * 0.1
          }} onClick={() => onNavigate('business', related.slug)} className="group cursor-pointer bg-white rounded-sm overflow-hidden shadow-sm hover:shadow-xl transition-all duration-500 border-b-2 border-transparent hover:border-[#c9a961]" data-id="element-533">
                  <div className="h-56 bg-[#1d1d1f] relative overflow-hidden" data-id="element-534">
                    {related.image ? <div className="absolute inset-0 bg-cover bg-center transition-transform duration-1000 group-hover:scale-110" style={{
                backgroundImage: `url('${related.image}')`
              }} data-id="element-535" /> : <div className="absolute inset-0 flex items-center justify-center" data-id="element-536">
                        <span className="font-display text-[48px] text-white/20" data-id="element-537">
                          {related.name.charAt(0)}
                        </span>
                      </div>}
                    <div className="absolute inset-0 bg-black/20 group-hover:bg-transparent transition-colors duration-500" data-id="element-538" />
                  </div>
                  <div className="p-8" data-id="element-539">
                    <div className="flex items-center gap-2 text-[#86868b] text-[13px] mb-3 uppercase tracking-wide" data-id="element-540">
                      <MapPinIcon className="w-3.5 h-3.5 text-[#c9a961]" data-id="element-541" />
                      {related.location}
                    </div>
                    <h3 className="font-display text-[20px] font-medium text-[#1d1d1f] mb-3 group-hover:text-[#c9a961] transition-colors" data-id="element-542">
                      {related.name}
                    </h3>
                    <p className="text-[#86868b] text-[15px] line-clamp-2 font-light" data-id="element-543">
                      {related.description}
                    </p>
                  </div>
                </motion.div>)}
            </div>
          </div>
        </section>}

      <Lightbox images={galleryImages} initialIndex={lightboxIndex} isOpen={isLightboxOpen} onClose={() => setIsLightboxOpen(false)} data-id="element-544" />
      <Footer data-id="element-545" />
    </main>;
}